import type {
  CreateBankAccountInput,
  CreateFirmInput,
  EfacturaConnectionStatus,
  EfacturaConnectionSummary,
  EfacturaSettingsInput,
  EfacturaSyncResult,
  UpdateBankAccountInput,
  UpdateFirmInput,
} from '@financiar/shared';
import { apiClient } from '../../api/client';
import type {
  CompanyBankAccount,
  CompanyVatPeriod,
  Firm,
  FirmDetail,
  FirmWrite,
  ListParams,
  Paginated,
} from '../../api/types';

/** Full firm list, used for the FirmaCurenta selector and any place that
 * needs "all firms" rather than a paginated table (Task 8b's /firme table
 * fetches its own page separately via `fetchFirmeList`). pageSize 100
 * comfortably covers a single-office deployment; revisit if that assumption
 * stops holding. */
export async function fetchFirme(): Promise<Paginated<Firm>> {
  const { data } = await apiClient.get<Paginated<Firm>>('/firms', {
    params: { page: 1, pageSize: 100 },
  });
  return data;
}

/** `/firme`'s own server-paginated, searchable table query. */
export async function fetchFirmeList(params: ListParams): Promise<Paginated<Firm>> {
  const { data } = await apiClient.get<Paginated<Firm>>('/firms', { params });
  return data;
}

export async function fetchFirma(id: string): Promise<FirmDetail> {
  const { data } = await apiClient.get<FirmDetail>(`/firms/${id}`);
  return data;
}

export async function createFirma(input: CreateFirmInput): Promise<FirmWrite> {
  const { data } = await apiClient.post<FirmWrite>('/firms', input);
  return data;
}

export async function updateFirma(id: string, input: UpdateFirmInput): Promise<FirmWrite> {
  const { data } = await apiClient.patch<FirmWrite>(`/firms/${id}`, input);
  return data;
}

export async function deleteFirma(id: string): Promise<void> {
  await apiClient.delete(`/firms/${id}`);
}

// --- VAT periods -----------------------------------------------------

/** `firmVatPeriodSchema`'s `z.infer` type reflects the schema's PARSED
 * output (`z.coerce.date()` -> `Date`), not the JSON string this actually
 * travels the wire as — so this is a hand-written request type instead of
 * importing `FirmVatPeriodInput` from `@financiar/shared`. Dates are
 * `YYYY-MM-DD`, matching what `firmVatPeriodSchema`'s `z.coerce.date()`
 * accepts server-side. */
export interface CreateVatPeriodInput {
  startsOn: string;
  endsOn?: string;
}

export async function createVatPeriod(firmaId: string, input: CreateVatPeriodInput): Promise<CompanyVatPeriod> {
  const { data } = await apiClient.post<CompanyVatPeriod>(`/firms/${firmaId}/vat-periods`, input);
  return data;
}

export async function deleteVatPeriod(firmaId: string, periodId: string): Promise<void> {
  await apiClient.delete(`/firms/${firmaId}/vat-periods/${periodId}`);
}

// --- Bank accounts -----------------------------------------------------

export async function fetchBankAccounts(firmaId: string): Promise<CompanyBankAccount[]> {
  const { data } = await apiClient.get<CompanyBankAccount[]>(`/firms/${firmaId}/bank-accounts`);
  return data;
}

export async function createBankAccount(firmaId: string, input: CreateBankAccountInput): Promise<CompanyBankAccount> {
  const { data } = await apiClient.post<CompanyBankAccount>(`/firms/${firmaId}/bank-accounts`, input);
  return data;
}

export async function updateBankAccount(id: string, input: UpdateBankAccountInput): Promise<CompanyBankAccount> {
  const { data } = await apiClient.patch<CompanyBankAccount>(`/bank-accounts/${id}`, input);
  return data;
}

export async function deleteBankAccount(id: string): Promise<void> {
  await apiClient.delete(`/bank-accounts/${id}`);
}

// --- Logo -----------------------------------------------------

export async function uploadLogo(firmaId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  // No explicit Content-Type: the browser sets `multipart/form-data` with
  // the correct boundary itself when the body is a FormData instance —
  // setting it by hand here would strip that boundary.
  await apiClient.put(`/firms/${firmaId}/logo`, formData);
}

export async function removeLogo(firmaId: string): Promise<void> {
  await apiClient.delete(`/firms/${firmaId}/logo`);
}

/** `<img src>` for `GET /firms/:id/logo` — a plain URL, not an axios call,
 * since it's consumed by an `<img>` tag (same-origin dev proxy + cookie auth
 * covers it exactly like every other request). `cacheBust` should change
 * (e.g. `Date.now()`) after every upload/delete so the browser doesn't keep
 * showing a stale cached image for the same firm id. */
export function logoUrl(firmaId: string, cacheBust?: string | number): string {
  return `/api/v1/firms/${firmaId}/logo${cacheBust !== undefined ? `?t=${cacheBust}` : ''}`;
}

// --- e-Factura (ANAF) -----------------------------------------------------

export async function fetchEfacturaConnection(firmaId: string): Promise<EfacturaConnectionStatus> {
  const { data } = await apiClient.get<EfacturaConnectionStatus>(`/firms/${firmaId}/efactura/connection`);
  return data;
}

/** Compact e-Factura status for every firm — one request for the header
 * selector badges (avoids a per-firm connection call). */
export async function fetchEfacturaConnections(): Promise<EfacturaConnectionSummary[]> {
  const { data } = await apiClient.get<EfacturaConnectionSummary[]>('/efactura/connections');
  return data;
}

/** Fetch the ANAF authorize URL, then the caller redirects the browser to it
 * (window.location) so the user can present their digital certificate. */
export async function fetchEfacturaAuthUrl(companyId: string): Promise<string> {
  const { data } = await apiClient.get<{ url: string }>(`/efactura/auth-url/${companyId}`);
  return data.url;
}

export async function disconnectEfactura(firmaId: string): Promise<EfacturaConnectionStatus> {
  const { data } = await apiClient.post<EfacturaConnectionStatus>(`/firms/${firmaId}/efactura/disconnect`, {});
  return data;
}

export async function testEfactura(firmaId: string): Promise<{ ok: boolean }> {
  const { data } = await apiClient.post<{ ok: boolean }>(`/firms/${firmaId}/efactura/test`, {});
  return data;
}

export async function updateEfacturaSettings(
  firmaId: string,
  input: EfacturaSettingsInput,
): Promise<EfacturaConnectionStatus> {
  const { data } = await apiClient.patch<EfacturaConnectionStatus>(`/firms/${firmaId}/efactura/settings`, input);
  return data;
}

/** Reconcile invoices already in SPV (sent from another system) with ANAF —
 * matches by number and fills status + indices. Last `days` (max 60). */
export async function syncEfacturaFromSpv(firmaId: string, days = 60): Promise<EfacturaSyncResult> {
  const { data } = await apiClient.post<EfacturaSyncResult>(`/firms/${firmaId}/efactura/sync?days=${days}`, {});
  return data;
}
